<script lang="ts">import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { CheckIcon, EyeIcon, EyeSlashIcon, IncorrectIcon } from "@infinitro/icons";
import { Input } from "@infinitro/input";
import { onDestroy, onMount } from "svelte";
import { finishPasswordResetUi, passwordCredentialsMinLength, stateAuthTab, stateFormMode, stateIdentifierResolved, statePassword, statePasswordConfirm, statePasswordConfirmSet, statePasswordIncorrect, statePasswordSet, submit } from "../state";
import { FormMode } from "../types";
import { isValidPassword } from "../utils/passwordCredentials";
let mode = FormMode.Login;
const unsubscribeFormMode = stateFormMode.subscribe((value) => {
	mode = value;
});
export let disabled = false;
export let confirm = false;
export let showHint = false;
export let choosePassword = false;
export let namePrefix = "";
let credentialsElement = null;
let element;
let valid = false;
let revealed = false;
export let required = true;
let incorrect = false;
let password = undefined;
let mismatch = false;
let value = "";
let touched = false;
let autofillLocked = true;
$: credentials = $stateAuthTab.usesDirectAuth;
$: useFlat = $stateUi.modal?.modalStyle === "flat";
$: minLength = credentials ? passwordCredentialsMinLength() : 8;
$: label = confirm ? $t(Verbiage.confirm_password) : choosePassword ? $t(Verbiage.choose_a_password) : $t(Verbiage.password);
$: placeholder = credentials ? $t(Verbiage.enter_your_password) : confirm ? $t(Verbiage.confirm_password) : mode === FormMode.Register || choosePassword ? $t(Verbiage.choose_a_password) : $t(Verbiage.password);
$: loginCredentials = credentials && !confirm && mode === FormMode.Login && !showHint && !choosePassword;
$: fieldName = `${namePrefix}${confirm ? "confirmPassword" : "password"}`;
$: errorMessage = confirm ? mismatch ? $t(Verbiage.passwords_do_not_match) : "" : loginCredentials && $statePasswordIncorrect ? $t(Verbiage.invalid_password_username_or_email) : showHint && touched && value.length > 0 && !valid ? $t(Verbiage.password_complexity_hint) : "";
$: showError = Boolean(errorMessage);
const setStores = (next, isValid) => {
	if (confirm) {
		statePasswordConfirm.set(isValid ? next : undefined);
		statePasswordConfirmSet.set(isValid);
	} else {
		statePassword.set(isValid ? next : next.length ? next : undefined);
		statePasswordSet.set(isValid);
		if (!loginCredentials) statePasswordIncorrect.set(false);
	}
};
const onChange = () => {
	const next = credentials ? value : element.value;
	touched = true;
	incorrect = false;
	if (confirm) {
		valid = Boolean($statePassword) && next === $statePassword;
		if (!credentials) {
			valid = next.length > 0 && next === $statePassword;
		}
		mismatch = Boolean(next) && next !== $statePassword;
	} else if (loginCredentials) {
		valid = next.length > 0;
		mismatch = false;
	} else if (credentials) {
		valid = isValidPassword(next, minLength);
		mismatch = false;
	} else {
		valid = next.length > 0 && element.checkValidity();
		mismatch = false;
	}
	setStores(next, valid);
};
// Called when the OTHER (main) password field changes. Compares directly against the
// value the subscription just handed us instead of re-reading $statePassword, which can
// still be stale at this exact instant (races against Svelte's own store auto-subscription).
const syncConfirmAgainstMainPassword = (mainPassword) => {
	const next = credentials ? value : element?.value;
	if (next === undefined) return;
	valid = Boolean(mainPassword) && next === mainPassword;
	if (!credentials) {
		valid = next.length > 0 && next === mainPassword;
	}
	mismatch = Boolean(next) && next !== mainPassword;
	if (credentials) {
		statePasswordConfirm.set(valid ? next : undefined);
		statePasswordConfirmSet.set(valid);
	}
};
const onBlur = () => {
	if (!incorrect) {
		onChange();
		if (!valid && !credentials) {
			if (confirm) {
				statePasswordConfirm.set(element.value);
			} else {
				statePassword.set(element.value);
			}
		}
	}
};
const onRevealToggle = (event) => {
	event.preventDefault();
	revealed = !revealed;
	if (element) element.type = revealed ? "text" : "password";
	if (credentialsElement && "type" in credentialsElement) {
		credentialsElement.type = revealed ? "text" : "password";
	}
};
const onCredentialsInput = (next) => {
	value = next;
	onChange();
};
const onKeydown = (event) => {
	if (event.key !== "Enter") return;
	event.preventDefault();
	if (namePrefix === "reset-") {
		const resetReady = isValidPassword($statePassword ?? "", minLength) && Boolean($statePasswordConfirm) && $statePassword === $statePasswordConfirm;
		if (resetReady) finishPasswordResetUi();
		return;
	}
	submit(event);
};
const unsubscribePassword = statePassword.subscribe((next) => {
	if (!confirm) {
		password = next;
		return;
	}
	if (credentials || element?.value) {
		syncConfirmAgainstMainPassword(next);
	}
});
const unsubscribePasswordConfirm = statePasswordConfirm.subscribe((next) => {
	if (confirm) password = next;
});
const unlockAutofill = () => {
	autofillLocked = false;
};
onMount(async () => {
	if (credentials) {
		// This field just appeared because the identifier resolved (login flow) - move
		// focus here so the player can start typing the password right away.
		if (!confirm && !choosePassword && $stateIdentifierResolved) {
			credentialsElement?.focus();
		}
		return;
	}
	if (password) {
		value = password;
		if (element) element.value = password;
	}
});
onDestroy(() => {
	unsubscribeFormMode();
	unsubscribePassword();
	unsubscribePasswordConfirm();
});
</script>

<span class="section" class:section-credentials={credentials}>
	{#if credentials}
		<label class="label" for={fieldName}>{label} *</label>
		<div class="input-wrap">
			<Input
				type="password"
				name={fieldName}
				id={fieldName}
				autocomplete="off"
				readonly={autofillLocked}
				onFocus={unlockAutofill}
				{placeholder}
				minlength={minLength}
				{disabled}
				{required}
				bind:element={credentialsElement}
				bind:value
				onInput={onCredentialsInput}
				{onKeydown}
				{onBlur}
				customBorderRadius={useFlat ? '8px' : undefined}
				inputClasses={useFlat ? 'auth-flat-input-el' : ''}
				customStyles="padding-right: 48px;"
				error={showError}
				message={errorMessage}
			/>
			<button type="button" class="reveal" on:click={onRevealToggle}>
				{#if revealed}
					<EyeSlashIcon />
				{:else}
					<EyeIcon />
				{/if}
			</button>
		</div>
	{:else}
		<div class="input">
			<input
				type="password"
				name={confirm ? 'confirmPassword' : 'password'}
				id={confirm ? 'confirmPassword' : 'password'}
				autocomplete={confirm
					? 'new-password'
					: mode === FormMode.Register
						? 'new-password'
						: 'current-password'}
				{placeholder}
				minlength={minLength}
				{disabled}
				class={$statePasswordIncorrect || mismatch ? 'invalid' : ''}
				aria-invalid={$statePasswordIncorrect || mismatch}
				{required}
				bind:this={element}
				on:change={onChange}
				on:input={onChange}
				on:blur={onBlur}
			/>
			<span class="trailing-icon"
				><button type="button" class="reveal-legacy" on:click={onRevealToggle}>
					{#if revealed}
						<EyeSlashIcon />
					{:else}
						<EyeIcon />
					{/if}</button
				>
				<span class="check">
					{#if $statePasswordIncorrect || mismatch}<IncorrectIcon />{:else if valid}
						<CheckIcon />
					{/if}
				</span>
			</span>
		</div>
		{#if confirm && mismatch}
			<p class="hint mismatch">{$t(Verbiage.passwords_do_not_match)}</p>
		{/if}
	{/if}
</span>

<style>
	.section {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    padding-top: 1rem;
    padding-bottom: 1rem
}
	.section-credentials {
    gap: 0.5rem;
    padding-top: 0px;
    padding-bottom: 0px
}
	.label {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    --tw-text-opacity: 1;
    color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}
	.input-wrap {
    position: relative;
    width: 100%
}
	div.input {
    position: relative;
    width: 100%;
    white-space: nowrap
}
	.hint {
    font-size: 11px;
    line-height: 1rem;
    color: var(--color-text-500)
}
	.mismatch {
    --tw-text-opacity: 1;
    color: rgb(248 113 113 / var(--tw-text-opacity, 1))
}
	.trailing-icon {
    pointer-events: auto;
    position: absolute;
    top: 14px;
    right: 0.5rem;
    display: flex;
    padding-right: 0.5rem
}
	.check {
    position: absolute;
    top: 4px;
    left: -8px
}
	.reveal {
    position: absolute;
    right: 0px;
    top: 0px;
    z-index: 40;
    display: flex;
    height: 3rem;
    width: 3rem;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    padding: 0px
}
	.reveal :global(svg),
	.reveal :global(img) {
    height: 1.25rem;
    width: 1.25rem
}
	.reveal-legacy {
    z-index: 40;
    cursor: pointer
}
	input,
	input:disabled {
    display: block;
    width: 100%;
    border-radius: 0.5rem;
    border-width: 1px;
    border-color: var(--color-neutral-900);
    background-color: transparent;
    padding: 1rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    --tw-text-opacity: 1;
    color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}
	input:focus {
    display: block;
    width: 100%;
    border-radius: 0.5rem;
    border-width: 1px;
    --tw-border-opacity: 1;
    border-color: rgb(59 130 246 / var(--tw-border-opacity, 1));
    background-color: var(--color-background-100);
    padding: 1rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    --tw-text-opacity: 1;
    color: rgb(255 255 255 / var(--tw-text-opacity, 1));
    --tw-ring-opacity: 1;
    --tw-ring-color: rgb(59 130 246 / var(--tw-ring-opacity, 1))
}
	input.invalid,
	input.invalid:focus {
    --tw-border-opacity: 1;
    border-color: rgb(239 68 68 / var(--tw-border-opacity, 1));
    --tw-ring-opacity: 1;
    --tw-ring-color: rgb(239 68 68 / var(--tw-ring-opacity, 1))
}
</style>
